import React, { useEffect, useState } from "react";
import "./AdminUserApproval.css";
import { AUTH_BASE_URL, ensureArray } from "./config";
import { decodeJwtPayload, getAuthToken } from "./auth/authUtils";

const ROLE_OPTIONS = [
  { value: "ROLE_VIEW", label: "View" },
  { value: "ROLE_GUEST", label: "Guest" },
  { value: "ROLE_TRADE", label: "Trade" },
  { value: "ROLE_ADMIN", label: "Admin" },
];

const AdminUserApproval = () => {
  const [users, setUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPendingUsers = async () => {
    setLoading(true);
    setError("");
    try {
      const token = getAuthToken();
      const res = await fetch(`${AUTH_BASE_URL}/auth/admin/users/pending`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`Failed to fetch users: ${res.status}`);

      const data = await res.json();
      const safeUsers = ensureArray(data);
      if (!Array.isArray(data)) {
        console.warn("Unexpected admin pending users payload shape", data);
      }
      setUsers(safeUsers);
      setSelectedRoles(
        safeUsers.reduce((acc, user) => {
          acc[user.id] = user.roles?.[0] || "ROLE_VIEW";
          return acc;
        }, {})
      );
    } catch (e) {
      console.error("Unable to load pending users", e);
      setError("Unable to load pending users");
    } finally {
      setLoading(false);
    }
  };

  const approveUser = async (id) => {
    await updateUserStatus(id, "approve", selectedRoles[id] || "ROLE_VIEW");
  };

  const disableUser = async (id) => {
    await updateUserStatus(id, "disable");
  };

  const updateUserStatus = async (id, action, roleName) => {
    try {
      const token = getAuthToken();
      const tokenRoles = ensureArray(decodeJwtPayload(token)?.roles);
      if (!token || !tokenRoles.includes("ROLE_ADMIN")) {
        throw new Error("Your current login token is not an admin token. Please logout and login again as admin.");
      }

      const res = await fetch(`${AUTH_BASE_URL}/auth/admin/users/${id}/${action}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          ...(roleName ? { "Content-Type": "application/json" } : {}),
        },
        body: roleName ? JSON.stringify({ roleName }) : undefined,
      });

      if (!res.ok) {
        const responseText = await res.text();
        throw new Error(responseText || `Action failed: ${res.status}`);
      }

      setUsers((prev) => prev.filter((u) => u.id !== id));
      setSelectedRoles((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (e) {
      console.error("Admin action failed", e);
      alert(e.message || "Action failed");
    }
  };

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  if (loading) return <p className="admin-loading">Loading...</p>;
  if (error) return <p className="admin-error">{error}</p>;

  return (
    <section className="admin-section">
      <h2>Pending User Approvals</h2>

      {ensureArray(users).length === 0 ? (
        <p>No pending users</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Assign Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ensureArray(users).map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{u.status}</td>
                <td>
                  <select
                    className="admin-role-select"
                    value={selectedRoles[u.id] || "ROLE_VIEW"}
                    onChange={(event) =>
                      setSelectedRoles((prev) => ({
                        ...prev,
                        [u.id]: event.target.value,
                      }))
                    }
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <button
                    className="btn approve"
                    onClick={() => approveUser(u.id)}
                  >
                    Approve
                  </button>
                  <button
                    className="btn disable"
                    onClick={() => disableUser(u.id)}
                  >
                    Disable
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default AdminUserApproval;
